import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { racesAPI, picksAPI, tidbitsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import ChatBoard from '../components/ChatBoard';

const RATING_CATS = [
  { key: 'B',   label: 'Breeding' },
  { key: 'H',   label: 'Hype' },
  { key: 'N',   label: 'Name' },
  { key: 'M',   label: 'Mystery' },
  { key: 'K',   label: 'Karma' },
  { key: 'A',   label: 'Annoyance' },
  { key: 'Hnd', label: 'Handicapping' },
];
const hasAnyRatings = (ratings) =>
  !!ratings && RATING_CATS.some(c => ratings[c.key] !== '' && ratings[c.key] != null);

function RaceDetail() {
  const { raceId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editPickId = searchParams.get('edit');
  const isEditMode = !!editPickId;
  const { user: currentUser } = useAuth();
  
  const [race, setRace] = useState(null);
  const [allPicks, setAllPicks] = useState(null);
  const [myPick, setMyPick] = useState(null);
  const [selectedHorses, setSelectedHorses] = useState([]);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [expandedHorses, setExpandedHorses] = useState({});
  const [horseTidbits, setHorseTidbits] = useState({});
  const [raceTidbits, setRaceTidbits] = useState([]);
  const [activeTab, setActiveTab] = useState('race');

  const loadRaceData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      // Load race details
      const raceResponse = await racesAPI.getRaceById(raceId);
      if (raceResponse.success) {
        setRace(raceResponse.data);
      }

      // Load race-wide tidbits (no horse ID)
      try {
        const tidbitsResponse = await tidbitsAPI.getRaceTidbits(raceId, null);
        if (tidbitsResponse.success) {
          setRaceTidbits(tidbitsResponse.data.tidbits || []);
        }
      } catch (err) {
        console.error('Failed to load race tidbits:', err);
        setRaceTidbits([]);
      }

      // Try to load all picks (will fail if user hasn't picked yet)
      try {
        const picksResponse = await picksAPI.getRacePicks(raceId);
        if (picksResponse.success) {
          setAllPicks(picksResponse.data);
          
          // Find my pick — match against the currently logged-in user's username
          const myPickData = picksResponse.data.picks?.find(
            (pick) => pick.username === currentUser?.username
          );
          if (myPickData) {
            setMyPick(myPickData);
            
            // If in edit mode, pre-populate selected horses
            if (isEditMode && myPickData.pickId === editPickId) {
              const preSelectedHorses = myPickData.horses?.map(h => ({
                id: h.horseId,
                name: h.horseName
              })) || [];
              setSelectedHorses(preSelectedHorses);
              setComment(myPickData.comment || '');
            }
          }
        }
      } catch (err) {
        // User hasn't picked yet - this is okay
        setAllPicks(null);
      }
    } catch (error) {
      console.error('Failed to load race data:', error);
      setError('Failed to load race details');
    } finally {
      setLoading(false);
    }
  }, [raceId, isEditMode, editPickId, currentUser]);

  useEffect(() => {
    loadRaceData();
  }, [loadRaceData]);

  const toggleHorseExpansion = async (horseId) => {
    const isCurrentlyExpanded = expandedHorses[horseId];
    
    setExpandedHorses({
      ...expandedHorses,
      [horseId]: !isCurrentlyExpanded
    });
    
    // Fetch tidbits if expanding and we don't have them yet
    if (!isCurrentlyExpanded && !horseTidbits[horseId]) {
      try {
        const response = await tidbitsAPI.getRaceTidbits(raceId, horseId);
        if (response.success) {
          setHorseTidbits({
            ...horseTidbits,
            [horseId]: response.data.tidbits || []
          });
        }
      } catch (error) {
        console.error('Failed to load tidbits for horse:', error);
        setHorseTidbits({
          ...horseTidbits,
          [horseId]: []
        });
      }
    }
  };

  const handleHorseSelection = (horseId, horseName) => {
    const isAlreadySelected = !!selectedHorses.find(h => h.id === horseId);
    // Don't allow SELECTING a scratched horse, but DO allow deselecting one
    // (user may have it pre-selected from before it was scratched)
    const horse = race?.horses?.find(h => h.horseId === horseId);
    if (horse?.scratched && !isAlreadySelected) return;

    if (isAlreadySelected) {
      // Deselect
      setSelectedHorses(selectedHorses.filter(h => h.id !== horseId));
    } else {
      // Select (max 3)
      if (selectedHorses.length < 3) {
        setSelectedHorses([...selectedHorses, { id: horseId, name: horseName }]);
      } else {
        setError('You can only select 3 horses. Deselect one first.');
        setTimeout(() => setError(''), 3000);
      }
    }
  };

  const handleSubmitPick = async () => {
    if (selectedHorses.length !== 3) {
      setError('You must select exactly 3 horses');
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      setSuccessMessage('');

      const pickData = {
        raceId,
        horseIds: selectedHorses.map(h => h.id),
        comment: comment.trim(),
      };

      console.log('Submitting pick data:', pickData);
      console.log('Selected horses:', selectedHorses);

      let response;
      if (isEditMode) {
        // Update existing pick
        response = await picksAPI.updatePick(editPickId, pickData);
      } else {
        // Create new pick
        response = await picksAPI.createPick(pickData);
      }

      if (response.success) {
        const horseNames = selectedHorses.map(h => h.name).join(', ');
        const action = isEditMode ? 'updated' : 'submitted';
        setSuccessMessage(`Pick ${action} successfully: ${horseNames}`);
        setMyPick(response.data);
        setSelectedHorses([]);
        setComment('');
        
        // If we were editing, navigate back to my picks
        if (isEditMode) {
          setTimeout(() => {
            navigate('/my-picks');
          }, 1500);
        } else {
          // Reload to show all picks
          setTimeout(() => {
            loadRaceData();
          }, 1500);
        }
      } else {
        setError(response.error?.message || `Failed to ${isEditMode ? 'update' : 'submit'} pick`);
        
        // If already exists error, add navigation hint
        if (response.error?.code === 'ALREADY_EXISTS') {
          setTimeout(() => {
            if (window.confirm(response.error?.message + '\n\nWould you like to go to My Picks to edit your pick?')) {
              navigate('/my-picks');
            }
          }, 500);
        }
      }
    } catch (err) {
      const errorCode = err.response?.data?.error?.code;
      const errorMessage = err.response?.data?.error?.message || `Failed to ${isEditMode ? 'update' : 'submit'} pick`;
      setError(errorMessage);
      
      // If already exists error, add navigation hint
      if (errorCode === 'ALREADY_EXISTS') {
        setTimeout(() => {
          if (window.confirm(errorMessage + '\n\nWould you like to go to My Picks to edit your pick?')) {
            navigate('/my-picks');
          }
        }, 500);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading race details...</div>;
  }

  if (!race) {
    return (
      <div className="card">
        <p>Race not found</p>
        <button onClick={() => navigate('/races')} className="submit-button">
          Back to Races
        </button>
      </div>
    );
  }

  const isDeadlinePassed = new Date(race.pickDeadline) < new Date();
  const canSubmitPick = !isDeadlinePassed && (!myPick || isEditMode);

  return (
    <div className="race-detail">
      <button
        onClick={() => navigate(isEditMode ? '/my-picks' : '/races')}
        style={{
          marginBottom: '1rem',
          padding: '0.5rem 1rem',
          background: '#f5f5f5',
          border: '1px solid #ddd',
          borderRadius: '4px',
          cursor: 'pointer',
        }}
      >
        ← Back to {isEditMode ? 'My Picks' : 'Races'}
      </button>

      <div className="card">
        <h2>{race.raceName}</h2>
        {isEditMode && (
          <div style={{ padding: '1rem', background: '#e3f2fd', borderRadius: '4px', marginBottom: '1rem' }}>
            <p style={{ margin: 0, fontWeight: 'bold', color: '#1976d2' }}>
              ✏️ Editing your pick
            </p>
          </div>
        )}
        <div style={{ color: '#666', marginBottom: '1rem' }}>
          <p>📍 {race.venue}, {race.location}</p>
          <p>📅 {new Date(race.raceDate).toLocaleString()}</p>
          <p>🏁 Distance: {race.distance}</p>
          <p>🎯 Type: {race.raceType}</p>
          <p>⏰ Pick Deadline: {new Date(race.pickDeadline).toLocaleString()}</p>
          <span className={`race-status ${race.status}`}>{race.status}</span>
        </div>

        {myPick && !isEditMode && (
          <div style={{ padding: '1rem', background: '#e8f5e9', borderRadius: '4px', marginBottom: '1rem' }}>
            <p style={{ margin: 0, fontWeight: 'bold', color: '#2e7d32' }}>
              ✓ Your picks: {myPick.horses?.map(h => h.horseName || h.name).join(', ')}
            </p>
          </div>
        )}

        {/* Scratched-horse warning for existing picks — show even after deadline */}
        {myPick && !isEditMode && (() => {
          const scratchedInPick = myPick.horses?.filter(ph =>
            race.horses?.find(rh => rh.horseId === ph.horseId && rh.scratched)
          ) || [];
          if (scratchedInPick.length === 0) return null;
          return (
            <div style={{
              padding: '1rem',
              background: '#ffebee',
              border: '2px solid #f44336',
              borderRadius: '4px',
              marginBottom: '1rem'
            }}>
              <p style={{ margin: 0, fontWeight: 'bold', color: '#c62828' }}>
                🚫 Scratched Horse Alert
              </p>
              <p style={{ margin: '0.5rem 0 0 0', color: '#b71c1c' }}>
                Your pick includes{' '}
                <strong>{scratchedInPick.map(h => h.horseName).join(', ')}</strong>,
                which {scratchedInPick.length === 1 ? 'has' : 'have'} been scratched.
                {!isDeadlinePassed ? (
                  <> Please update your pick before the deadline:{' '}
                  <strong>{new Date(race.pickDeadline).toLocaleString()}</strong>.</>
                ) : (
                  <> The pick deadline has passed — your pick cannot be changed.</>  
                )}
              </p>
              {!isDeadlinePassed && (
                <button
                  onClick={() => navigate(`/races/${raceId}?edit=${myPick.pickId}`)}
                  style={{
                    marginTop: '0.75rem',
                    background: '#c62828',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '0.5rem 1rem',
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  ✏️ Update My Pick
                </button>
              )}
            </div>
          );
        })()}

        {!canSubmitPick && !isEditMode && isDeadlinePassed && (
          <div style={{ padding: '1rem', background: '#fff3e0', borderRadius: '4px', marginBottom: '1rem' }}>
            <p style={{ margin: 0, color: '#e65100' }}>
              ⏰ Pick deadline has passed
            </p>
          </div>
        )}

        {error && <div className="error-message">{error}</div>}
        {successMessage && (
          <div style={{ background: '#e8f5e9', color: '#2e7d32', padding: '0.75rem', borderRadius: '4px', marginBottom: '1rem' }}>
            {successMessage}
          </div>
        )}
      </div>

      {/* Tab bar */}
      <div style={{
        display: 'flex',
        borderBottom: '2px solid #e0e0e0',
        marginBottom: '1rem',
        background: '#fff',
        borderRadius: '8px 8px 0 0',
        overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
      }}>
        {[
          { key: 'race',     label: 'Race'     },
          { key: 'insights', label: 'Insights' },
          { key: 'picks',    label: `Picks${allPicks?.totalPicks ? ` (${allPicks.totalPicks})` : ''}` },
          { key: 'chat',     label: 'Chat'     },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              flex: 1,
              padding: '0.75rem 0.5rem',
              border: 'none',
              borderBottom: activeTab === tab.key ? '3px solid #2196F3' : '3px solid transparent',
              background: activeTab === tab.key ? '#e3f2fd' : 'transparent',
              color: activeTab === tab.key ? '#1565c0' : '#555',
              fontWeight: activeTab === tab.key ? '700' : '500',
              fontSize: '0.9rem',
              cursor: 'pointer',
              transition: 'all 0.15s',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Tab: Race ── */}
      {activeTab === 'race' && <div className="card">
        <h3>Horses ({race.horses?.length || 0})</h3>
        {canSubmitPick && (
          <div style={{ padding: '1rem', background: '#fff3cd', borderRadius: '4px', marginBottom: '1rem' }}>
            <p style={{ margin: 0, fontWeight: 'bold' }}>
              {isEditMode ? 'Edit your 3 horses' : 'Select 3 horses'} ({selectedHorses.length}/3 selected)
            </p>
            {selectedHorses.length > 0 && (
              <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem' }}>
                Selected: {selectedHorses.map(h => h.name).join(', ')}
              </p>
            )}
          </div>
        )}

        <table className="horses-table">
          <thead>
            <tr>
              <th style={{ width: '40px' }}>Info</th>
              <th>Post</th>
              <th>Horse</th>
              <th>Jockey</th>
              <th>Trainer</th>
              <th>Odds</th>
              <th style={{ textAlign: 'center' }}>Rtg</th>
              {canSubmitPick && <th>Select</th>}
            </tr>
          </thead>
          <tbody>
            {race.horses?.map((horse) => {
              const isSelected = selectedHorses.find(h => h.id === horse.horseId);
              const isExpanded = expandedHorses[horse.horseId];
              const tidbits = horseTidbits[horse.horseId] || [];
              const isScratched = !!horse.scratched;
              
              // Helper function to convert decimal odds to fractional
              const decimalToFractional = (decimal) => {
                if (!decimal || decimal <= 1) return '-';
                
                // Remove the 1 to get the profit multiplier
                const profit = decimal - 1;
                
                // Common fractional odds mapping
                const commonOdds = {
                  0.5: '1/2', 0.55: '11/20', 0.6: '3/5', 0.65: '13/20', 0.7: '7/10',
                  0.75: '3/4', 0.8: '4/5', 0.85: '17/20', 0.9: '9/10', 0.95: '19/20',
                  1.0: 'Evens', 1.1: '11/10', 1.2: '6/5', 1.25: '5/4', 1.3: '13/10',
                  1.4: '7/5', 1.5: '3/2', 1.6: '8/5', 1.7: '17/10', 1.75: '7/4',
                  1.8: '9/5', 1.9: '19/10', 2.0: '2/1', 2.2: '11/5', 2.25: '9/4',
                  2.4: '12/5', 2.5: '5/2', 2.6: '13/5', 2.75: '11/4', 2.8: '14/5',
                  3.0: '3/1', 3.2: '11/5', 3.5: '5/2', 4.0: '3/1', 4.5: '7/2',
                  5.0: '4/1', 5.5: '9/2', 6.0: '5/1', 6.5: '11/2', 7.0: '6/1',
                  7.5: '13/2', 8.0: '7/1', 9.0: '8/1', 10.0: '9/1', 11.0: '10/1',
                  12.0: '11/1', 13.0: '12/1', 15.0: '14/1', 17.0: '16/1', 21.0: '20/1',
                  26.0: '25/1', 34.0: '33/1', 51.0: '50/1', 101.0: '100/1'
                };
                
                // Check for exact match
                if (commonOdds[profit]) return commonOdds[profit];
                
                // Find closest common odds
                const closest = Object.keys(commonOdds).reduce((prev, curr) => {
                  return Math.abs(parseFloat(curr) - profit) < Math.abs(parseFloat(prev) - profit) ? curr : prev;
                });
                
                return commonOdds[closest];
              };
              
              const oddsDisplay = horse.odds?.fractional || decimalToFractional(horse.odds?.decimal);
              
              return (
                <React.Fragment key={horse.horseId}>
                  <tr style={{
                    background: isScratched ? '#f5f5f5' : isSelected ? '#e3f2fd' : 'transparent',
                    opacity: isScratched ? 0.65 : 1
                  }}>
                    <td style={{ textAlign: 'center' }}>
                      <button
                        onClick={() => toggleHorseExpansion(horse.horseId)}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '1.2rem',
                          padding: '0.25rem'
                        }}
                        title={isExpanded ? 'Hide info' : 'Show info'}
                      >
                        {isExpanded ? '🔽' : '▶️'}
                      </button>
                    </td>
                    <td>{horse.postPosition}</td>
                    <td>
                      <strong style={{ textDecoration: isScratched ? 'line-through' : 'none' }}>
                        {horse.horseName}
                      </strong>
                      {isScratched && (
                        <span style={{
                          marginLeft: '0.5rem',
                          background: '#f44336',
                          color: 'white',
                          borderRadius: '4px',
                          padding: '0.1rem 0.4rem',
                          fontSize: '0.75rem',
                          fontWeight: 'bold',
                          verticalAlign: 'middle'
                        }}>SCRATCHED</span>
                      )}
                      {!isScratched && race.results?.winner?.horseId === horse.horseId && (
                        <span style={{ marginLeft: '0.5rem', color: '#4CAF50' }}>🏆</span>
                      )}
                    </td>
                    <td>{horse.jockey}</td>
                    <td>{horse.trainer}</td>
                    <td>{oddsDisplay}</td>
                    <td style={{ textAlign: 'center', fontWeight: hasAnyRatings(horse.ratings) ? 'bold' : 'normal', color: hasAnyRatings(horse.ratings) ? '#2196F3' : 'inherit' }}>
                      {horse.ratings?.total ? parseFloat(horse.ratings.total).toFixed(1) : '—'}
                    </td>
                    {canSubmitPick && (
                      <td>
                        {isScratched && !isSelected ? (
                          <span style={{
                            display: 'inline-block',
                            padding: '0.3rem 0.75rem',
                            background: '#e0e0e0',
                            color: '#9e9e9e',
                            borderRadius: '4px',
                            fontSize: '0.85rem',
                            cursor: 'not-allowed'
                          }}>Scratched</span>
                        ) : (
                          <button
                            onClick={() => handleHorseSelection(horse.horseId, horse.horseName)}
                            disabled={submitting}
                            className="pick-button"
                            style={{
                              background: isSelected
                                ? (isScratched ? '#f44336' : '#4CAF50')
                                : '#2196F3',
                              color: 'white'
                            }}
                            title={isSelected && isScratched ? 'This horse was scratched — click to remove from your selection' : undefined}
                          >
                            {isSelected
                              ? (isScratched ? '⚠️ Remove' : '✓ Selected')
                              : 'Select'}
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                  {isExpanded && (
                    <tr>
                      <td colSpan={canSubmitPick ? 8 : 7} style={{ padding: 0 }}>
                        <div style={{ 
                          padding: '1rem', 
                          background: '#f5f5f5', 
                          borderTop: '1px solid #ddd',
                          borderBottom: '1px solid #ddd'
                        }}>
                          <h4 style={{ margin: '0 0 1rem 0' }}>🏇 {horse.horseName} - Information & Tidbits</h4>

                          {/* Ratings sub-table */}
                          {hasAnyRatings(horse.ratings) && (
                            <div style={{ marginBottom: '1rem', overflowX: 'auto' }}>
                              <div style={{ fontSize: '0.8rem', fontWeight: '600', marginBottom: '0.4rem', opacity: 0.75 }}>📊 Ratings</div>
                              <table style={{ borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                                <thead>
                                  <tr>
                                    {RATING_CATS.map(c => (
                                      <th key={c.key} title={c.label} style={{
                                        padding: '0.3rem 0.55rem', textAlign: 'center',
                                        background: '#2196F3', color: '#fff',
                                        border: '1px solid rgba(255,255,255,0.25)',
                                        fontWeight: '700', fontSize: '0.8rem',
                                      }}>
                                        {c.key}
                                      </th>
                                    ))}
                                    <th style={{
                                      padding: '0.3rem 0.65rem', textAlign: 'center',
                                      background: '#1565c0', color: '#fff',
                                      border: '1px solid rgba(255,255,255,0.25)',
                                      fontWeight: '800', fontSize: '0.8rem',
                                    }}>Total</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr>
                                    {RATING_CATS.map(c => (
                                      <td key={c.key} style={{
                                        padding: '0.3rem 0.55rem', textAlign: 'center',
                                        border: '1px solid rgba(128,128,128,0.25)',
                                        fontWeight: '500',
                                      }}>
                                        {horse.ratings[c.key] !== '' && horse.ratings[c.key] != null ? horse.ratings[c.key] : '—'}
                                      </td>
                                    ))}
                                    <td style={{
                                      padding: '0.3rem 0.65rem', textAlign: 'center',
                                      border: '1px solid rgba(128,128,128,0.25)',
                                      fontWeight: 'bold', color: '#2196F3',
                                    }}>
                                      {horse.ratings?.total ? parseFloat(horse.ratings.total).toFixed(1) : '—'}
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                              <div style={{ fontSize: '0.72rem', opacity: 0.55, marginTop: '0.3rem' }}>
                                B=Breeding · H=Hype · N=Name · M=Mystery · K=Karma · A=Annoyance · Hnd=Handicapping
                              </div>
                            </div>
                          )}
                          
                          {tidbits.length === 0 ? (
                            <p style={{ margin: 0, color: '#666', fontStyle: 'italic' }}>
                              No tidbits available for this horse yet.
                            </p>
                          ) : (
                            <div style={{ display: 'grid', gap: '1rem' }}>
                              {tidbits.map((tidbit) => (
                                <div 
                                  key={tidbit.tidbitId}
                                  style={{
                                    padding: '1rem',
                                    background: 'white',
                                    borderRadius: '4px',
                                    border: '1px solid #ddd'
                                  }}
                                >
                                  <h5 style={{ marginTop: 0, marginBottom: '0.5rem' }}>
                                    {tidbit.title}
                                  </h5>
                                  <div
                                    className="tidbit-content"
                                    style={{
                                      fontSize: '0.95rem',
                                      lineHeight: '1.6',
                                      color: '#333',
                                      marginBottom: '0.5rem'
                                    }}
                                    dangerouslySetInnerHTML={{ __html: tidbit.content }}
                                  />
                                  <div style={{ fontSize: '0.85rem', color: '#999', borderTop: '1px solid #eee', paddingTop: '0.5rem' }}>
                                    By {tidbit.author} • {new Date(tidbit.createdAt).toLocaleDateString()}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>

        {canSubmitPick && selectedHorses.length === 3 && (
          <div style={{ marginTop: '1rem' }}>
            <div style={{ marginBottom: '1rem' }}>
              <label
                htmlFor="pick-comment"
                style={{ display: 'block', fontWeight: 'bold', marginBottom: '0.4rem' }}
              >
                💬 Reasoning / Comment <span style={{ fontWeight: 'normal', color: '#888', fontSize: '0.9rem' }}>(optional — visible to all users)</span>
              </label>
              <textarea
                id="pick-comment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                maxLength={500}
                rows={3}
                placeholder="Why did you pick these horses? Share your reasoning with others..."
                style={{
                  width: '100%',
                  padding: '0.6rem',
                  borderRadius: '4px',
                  border: '1px solid #ddd',
                  fontSize: '0.95rem',
                  resize: 'vertical',
                  boxSizing: 'border-box',
                }}
              />
              <div style={{ textAlign: 'right', fontSize: '0.8rem', color: '#999' }}>
                {comment.length}/500
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <button
                onClick={handleSubmitPick}
                disabled={submitting}
                className="submit-button"
                style={{
                  padding: '1rem 2rem',
                  fontSize: '1.1rem',
                  background: '#4CAF50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  fontWeight: 'bold'
                }}
              >
                {submitting
                  ? (isEditMode ? 'Updating...' : 'Submitting...')
                  : (isEditMode ? 'Update Your 3 Picks' : 'Submit Your 3 Picks')
                }
              </button>
            </div>
          </div>
        )}
      </div>}

      {/* Race Results (inside Race tab) */}
      {activeTab === 'race' && race.results && (
        <div className="card">
          <h3>Race Results</h3>
          <div style={{ display: 'grid', gap: '1rem' }}>
            <div style={{ padding: '1rem', background: '#e8f5e9', borderRadius: '4px' }}>
              <p style={{ margin: 0, fontWeight: 'bold', color: '#2e7d32' }}>
                🏆 Winner: {race.results.winner?.horseName} ({race.results.winner?.finalTime})
              </p>
              {race.results.place && (
                <p style={{ margin: '0.5rem 0 0 0', color: '#666' }}>
                  2nd: {race.results.place.horseName}
                </p>
              )}
              {race.results.show && (
                <p style={{ margin: '0.5rem 0 0 0', color: '#666' }}>
                  3rd: {race.results.show.horseName}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Tab: Insights ── */}
      {activeTab === 'insights' && (
        <div className="card">
          <h3>🏁 Race Information & Insights</h3>

          {/* Consolidated ratings table */}
          {(() => {
            const sorted = (race.horses || [])
              .filter(h => hasAnyRatings(h.ratings) || h.ratings?.total)
              .sort((a, b) => (parseFloat(b.ratings?.total) || 0) - (parseFloat(a.ratings?.total) || 0));
            if (sorted.length === 0) return null;
            return (
              <div style={{ marginBottom: '1.5rem' }}>
                <div style={{ fontWeight: '600', marginBottom: '0.6rem' }}>📊 All Horse Ratings</div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.85rem' }}>
                    <thead>
                      <tr>
                        <th style={{
                          padding: '0.35rem 0.6rem', textAlign: 'left',
                          background: '#2196F3', color: '#fff',
                          border: '1px solid rgba(255,255,255,0.25)',
                          fontWeight: '700', minWidth: '120px',
                        }}>Horse</th>
                        {RATING_CATS.map(c => (
                          <th key={c.key} title={c.label} style={{
                            padding: '0.35rem 0.6rem', textAlign: 'center',
                            background: '#2196F3', color: '#fff',
                            border: '1px solid rgba(255,255,255,0.25)',
                            fontWeight: '700',
                          }}>{c.key}</th>
                        ))}
                        <th style={{
                          padding: '0.35rem 0.7rem', textAlign: 'center',
                          background: '#1565c0', color: '#fff',
                          border: '1px solid rgba(255,255,255,0.25)',
                          fontWeight: '800',
                        }}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sorted.map((h, i) => (
                        <tr key={h.horseId} style={{ background: i % 2 === 0 ? 'rgba(33,150,243,0.06)' : 'transparent' }}>
                          <td style={{
                            padding: '0.3rem 0.6rem',
                            border: '1px solid rgba(128,128,128,0.2)',
                            fontWeight: '600',
                          }}>{h.horseName}</td>
                          {RATING_CATS.map(c => (
                            <td key={c.key} style={{
                              padding: '0.3rem 0.6rem', textAlign: 'center',
                              border: '1px solid rgba(128,128,128,0.2)',
                            }}>
                              {h.ratings[c.key] !== '' && h.ratings[c.key] != null ? h.ratings[c.key] : '—'}
                            </td>
                          ))}
                          <td style={{
                            padding: '0.3rem 0.7rem', textAlign: 'center',
                            border: '1px solid rgba(128,128,128,0.2)',
                            fontWeight: 'bold', color: '#2196F3',
                          }}>
                            {h.ratings?.total ? parseFloat(h.ratings.total).toFixed(1) : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div style={{ fontSize: '0.72rem', opacity: 0.55, marginTop: '0.4rem' }}>
                  B=Breeding · H=Hype · N=Name · M=Mystery · K=Karma · A=Annoyance · Hnd=Handicapping
                </div>
              </div>
            );
          })()}

          {raceTidbits.length === 0 ? (
            <p style={{ color: '#888', fontStyle: 'italic' }}>No insights have been posted for this race yet.</p>
          ) : (
            <div style={{ display: 'grid', gap: '1rem' }}>
              {raceTidbits.map((tidbit) => (
                <div
                  key={tidbit.tidbitId}
                  style={{ padding: '1rem', background: '#f5f5f5', borderRadius: '4px', border: '1px solid #ddd' }}
                >
                  <h4 style={{ marginTop: 0, marginBottom: '0.5rem', color: '#1976d2' }}>{tidbit.title}</h4>
                  <div
                    className="tidbit-content"
                    style={{ fontSize: '1rem', lineHeight: '1.6', color: '#333', marginBottom: '0.5rem' }}
                    dangerouslySetInnerHTML={{ __html: tidbit.content }}
                  />
                  <div style={{ fontSize: '0.85rem', color: '#999', borderTop: '1px solid #ddd', paddingTop: '0.5rem', marginTop: '0.5rem' }}>
                    By {tidbit.author} • {new Date(tidbit.createdAt).toLocaleDateString()}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Tab: Picks ── */}
      {activeTab === 'picks' && (
        <div className="card">
          <h3>All Picks {allPicks?.totalPicks != null ? `(${allPicks.totalPicks})` : ''}</h3>
          {!allPicks || allPicks.picks?.length === 0 ? (
            <p style={{ color: '#888', fontStyle: 'italic' }}>No picks have been submitted yet.</p>
          ) : (
            <table className="horses-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Horses Selected</th>
                  <th>Comment</th>
                  <th>Submitted</th>
                  {race.status === 'completed' && <th>Result</th>}
                  {race.status === 'completed' && <th>Payout</th>}
                </tr>
              </thead>
              <tbody>
                {allPicks.picks?.map((pick, index) => (
                  <tr key={index}>
                    <td>{pick.displayName || pick.username}</td>
                    <td>
                      <strong>{pick.horses?.map(h => h.horseName).join(', ') || 'N/A'}</strong>
                    </td>
                    <td style={{ maxWidth: '250px' }}>
                      {pick.comment ? (
                        <span style={{ fontSize: '0.9rem', color: '#333', fontStyle: 'italic' }}>
                          "{pick.comment}"
                        </span>
                      ) : (
                        <span style={{ color: '#bbb', fontSize: '0.85rem' }}>—</span>
                      )}
                    </td>
                    <td>{new Date(pick.submittedAt).toLocaleString()}</td>
                    {race.status === 'completed' && (
                      <td>
                        <span style={{ color: pick.actualPayout > 0 ? '#4CAF50' : '#f44336' }}>
                          {pick.actualPayout > 0 ? '✓ Win' : '✗ Loss'}
                        </span>
                      </td>
                    )}
                    {race.status === 'completed' && (
                      <td>
                        <div>
                          <strong style={{ color: pick.actualPayout > 0 ? '#4CAF50' : '#f44336' }}>
                            ${pick.actualPayout?.toFixed(2) || '0.00'}
                          </strong>
                          {pick.payoutBreakdown && pick.actualPayout > 0 && (
                            <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.25rem' }}>
                              {pick.payoutBreakdown.breakdown?.details?.map((detail, i) => (
                                <div key={i}>
                                  {detail.type}: ${detail.payout?.toFixed(2)}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── Tab: Chat ── */}
      {activeTab === 'chat' && (
        <div className="card">
          <ChatBoard title="Race Chat" raceId={raceId} />
        </div>
      )}
    </div>
  );
}

export default RaceDetail;
